import { makeAutoObservable, observable, runInAction } from 'mobx';
import type { ProviderType } from '../../api';
import { RosterClaimsFlow } from './roster-claims-flow';
import type {
    RosterDataSource,
    RosterFacility,
    RosterFacilityDraft,
    RosterListQuery,
    RosterProvider,
    RosterProviderDraft,
    RosterPage,
    RosterRecord,
    RosterRecordRef,
    RosterRole,
    RosterSort,
    RosterTab,
    RosterTypeCounts,
} from '../../services/roster/roster.types';

/**
 * State for the Roster screen (FE-109): which tab, which filters, the page of records they
 * produce, the counts on the tabs and type chips, and which record the drawer has open.
 *
 * **Paging, sorting, selection and column visibility belong to the table, not here.** The core
 * `DataTable` keeps page index, page size and sort in react-table state and reports them through
 * `onFetch`; its presenter keeps the selection and hidden columns and persists them. Holding a
 * second copy here would give every one of those two sources of truth, so `loadPage` takes the
 * table's request as an argument instead.
 *
 * Every read goes through the injected `RosterDataSource`, so the same store runs on the mock now
 * and on the support API later without changing.
 *
 * **Unknown is not zero (NS-3).** `page`, `typeCounts` and `facilitiesTotal` stay `undefined`
 * until a response arrives, and go back to `undefined` when a request fails. The UI renders a
 * placeholder for undefined and a number only for a number, so a failed request can never read
 * as "0 providers".
 *
 * Imports are leaf modules on purpose: the `services` barrel reaches code that needs `window`,
 * and this store is tested under node.
 */

/** `fetchAllMatching` could only get part of the matches — nothing should be exported as "all". */
export class RosterExportIncompleteError extends Error {
    constructor() {
        super('The search matched more records than can be fetched in full');
        this.name = 'RosterExportIncompleteError';
        Object.setPrototypeOf(this, RosterExportIncompleteError.prototype);
    }
}

/** A disable that failed, possibly partway. `stillActive` are the requested ids still on the roster. */
export class RosterDisableError extends Error {
    public readonly stillActive: string[];

    constructor(message: string, stillActive: string[]) {
        super(message);
        this.name = 'RosterDisableError';
        this.stillActive = stillActive;
        Object.setPrototypeOf(this, RosterDisableError.prototype);
    }
}

export interface RosterStoreConfig {
    dataSource: RosterDataSource;
    role: RosterRole;
    /** The clock for date-of-service presets. Tests pin it. */
    now?: () => Date;
}

export interface RosterDrawerState {
    tab: RosterTab;
    recordId?: string;
    /** `missing`: the record was not found — disabled since the list loaded, most likely. */
    status: 'loading' | 'ready' | 'missing';
    /** Set when `ready` for an existing record. */
    record?: RosterRecord;
}

/** What the table asks for: the page it is on, its size, and its sort. */
export interface RosterPageRequest {
    pageIndex: number;
    pageSize: number;
    sort?: RosterSort;
}

export class RosterStore {
    public readonly dataSource: RosterDataSource;
    public readonly role: RosterRole;
    private readonly now: () => Date;

    public tab: RosterTab = 'providers';
    public search = '';
    /** Providers only. Undefined is the "All" chip. */
    public providerType: ProviderType | undefined = undefined;

    /** The current page for `tab`. Undefined while loading the first time and after a failure. */
    public page: RosterPage<RosterRecord> | undefined = undefined;
    public loading = false;
    public error: string | undefined = undefined;

    /** Counts for the tabs and type chips — over everything, not the current search. */
    public typeCounts: RosterTypeCounts | undefined = undefined;
    public facilitiesTotal: number | undefined = undefined;
    public countsError: string | undefined = undefined;

    /**
     * The drawer, when open. No `recordId` is a new record. The record itself lives here, not in the
     * component: held there, the last record outlived the drawer and pre-filled "Add provider" and a
     * reopened record with stale values.
     */
    public drawer: RosterDrawerState | undefined = undefined;

    /** A bulk action is running; the bar's buttons are disabled so it cannot be sent twice. */
    public bulkBusy = false;

    /** The "apply changes to existing claims?" dialog, after an edit is saved. */
    public claimsFlow: RosterClaimsFlow | undefined = undefined;

    /** The table's last request, reused by a reload after a write and by the CSV's sort. */
    private lastRequest: RosterPageRequest = { pageIndex: 0, pageSize: 20 };
    private pageRequest = 0;
    private countsRequest = 0;
    private drawerTicket = 0;

    constructor(config: RosterStoreConfig) {
        this.dataSource = config.dataSource;
        this.role = config.role;
        this.now = config.now ?? ((): Date => new Date());
        makeAutoObservable<RosterStore, 'lastRequest' | 'pageRequest' | 'countsRequest' | 'drawerTicket' | 'now'>(
            this,
            {
                dataSource: false,
                lastRequest: false,
                pageRequest: false,
                countsRequest: false,
                drawerTicket: false,
                now: false,
                claimsFlow: observable.ref,
            },
            { autoBind: true },
        );
    }

    public get isMock(): boolean {
        return this.dataSource.kind === 'mock';
    }

    public get rows(): RosterRecord[] {
        return this.page?.items ?? [];
    }

    public get total(): number | undefined {
        return this.page?.total;
    }

    public get hasFilters(): boolean {
        return this.search.trim() !== '' || this.providerType !== undefined;
    }

    public get tabCounts(): Record<RosterTab, number | undefined> {
        return { providers: this.typeCounts?.total, facilities: this.facilitiesTotal };
    }

    /**
     * Set when the table is on a page past the end — a Disable emptied the last page. The table
     * owns its page index, so the page component moves it here; the store only says where.
     */
    public get outOfRangePage(): number | undefined {
        if (!this.page || this.page.items.length > 0 || this.page.total === 0) {
            return undefined;
        }
        return Math.max(0, Math.ceil(this.page.total / this.lastRequest.pageSize) - 1);
    }

    /** Called by the table whenever its page, page size or sort changes — and by `reload`. */
    public async loadPage(request: RosterPageRequest = this.lastRequest): Promise<void> {
        this.lastRequest = request;
        const ticket = ++this.pageRequest;
        const tab = this.tab;
        this.loading = true;
        this.error = undefined;

        const query: RosterListQuery = {
            ...this.filterQuery(),
            sort: request.sort,
            offset: request.pageIndex * request.pageSize,
            limit: request.pageSize,
        };

        try {
            const page: RosterPage<RosterRecord> =
                tab === 'providers'
                    ? await this.dataSource.listProviders(query)
                    : await this.dataSource.listFacilities(query);
            if (ticket !== this.pageRequest) {
                return;
            }
            runInAction(() => {
                this.page = page;
                this.loading = false;
            });
        } catch (error) {
            if (ticket !== this.pageRequest) {
                return;
            }
            runInAction(() => {
                this.page = undefined;
                this.error = messageOf(error);
                this.loading = false;
            });
        }
    }

    public async loadCounts(): Promise<void> {
        const ticket = ++this.countsRequest;
        this.countsError = undefined;
        try {
            const [typeCounts, facilities] = await Promise.all([
                this.dataSource.countProvidersByType(),
                this.dataSource.listFacilities({ offset: 0, limit: 0 }),
            ]);
            if (ticket !== this.countsRequest) {
                return;
            }
            runInAction(() => {
                this.typeCounts = typeCounts;
                this.facilitiesTotal = facilities.total;
            });
        } catch (error) {
            if (ticket !== this.countsRequest) {
                return;
            }
            runInAction(() => {
                this.typeCounts = undefined;
                this.facilitiesTotal = undefined;
                this.countsError = messageOf(error);
            });
        }
    }

    /** The page and the counts again — after a write, or from "Retry". */
    public async reload(): Promise<void> {
        await Promise.all([this.loadPage(), this.loadCounts()]);
    }

    /**
     * Switching tabs starts clean, as in the design: no search, no type, nothing loaded. The new
     * tab's table asks for its own first page.
     */
    public setTab(tab: RosterTab): void {
        if (tab === this.tab) {
            return;
        }
        this.pageRequest += 1; // drop any response still in flight for the old tab
        this.tab = tab;
        this.search = '';
        this.providerType = undefined;
        this.page = undefined;
        this.loading = false;
        this.error = undefined;
    }

    /**
     * Filter setters only record the value. The page mirrors them into the table's filters, which
     * sends the table back to its first page and asks for it — one fetch path, debounced by the
     * table, however the filters change.
     */
    public setSearch(search: string): void {
        this.search = search;
    }

    public setProviderType(providerType: ProviderType | undefined): void {
        this.providerType = providerType;
    }

    public clearFilters(): void {
        this.search = '';
        this.providerType = undefined;
    }

    /**
     * Every record matching the current tab, search, type and sort — not just this page. What
     * "Download CSV" exports, as in the design. Pages through the source so a server that caps
     * `limit` still returns everything.
     *
     * When the source says a page is `truncated` (a searched field matched more than the API returns
     * in one request), the total is a floor and the loop below would stop early — so it throws
     * `RosterExportIncompleteError` instead of passing a subset off as every match (NS-3).
     */
    public async fetchAllMatching(): Promise<RosterRecord[]> {
        const tab = this.tab;
        const base = { ...this.filterQuery(), sort: this.lastRequest.sort };
        const chunk = 100; // the API refuses a larger limit ("must be less than or equal to 100")
        const records: RosterRecord[] = [];
        for (let offset = 0; ; offset += chunk) {
            const page: RosterPage<RosterRecord> =
                tab === 'providers'
                    ? await this.dataSource.listProviders({ ...base, offset, limit: chunk })
                    : await this.dataSource.listFacilities({ ...base, offset, limit: chunk });
            if (page.truncated) {
                throw new RosterExportIncompleteError();
            }
            records.push(...page.items);
            if (page.items.length === 0 || records.length >= page.total) {
                return records;
            }
        }
    }

    /** The selected records for "Export selected", in selection order — they may span pages and searches. */
    public async recordsByIds(ids: string[]): Promise<RosterRecord[]> {
        const records = await this.dataSource.listByIds(this.tab, ids);
        const byId = new Map(records.map(record => [record.id, record]));
        return ids.map(id => byId.get(id)).filter((record): record is RosterRecord => !!record);
    }

    /**
     * Bulk writes. Each reloads the page and the counts afterwards — a tag changes the Tags column,
     * a disable changes the totals. A failure is thrown to the caller, which says so.
     */
    public addTag(ids: string[], tagName: string): Promise<void> {
        const tab = this.tab;
        return this.runBulk(() => this.dataSource.addTag(tab, ids, tagName));
    }

    /**
     * A disable writes one record at a time, so it can fail partway: some of the selection is gone
     * from the roster, the rest is not. The failure is rethrown as `RosterDisableError` carrying the
     * requested ids that are still active after the reload — what the selection should keep, so a
     * retry does not target records that are already disabled.
     */
    public async disable(ids: string[]): Promise<void> {
        const tab = this.tab;
        try {
            await this.runBulk(() => this.dataSource.disable(tab, ids));
        } catch (error) {
            throw new RosterDisableError(messageOf(error), await this.stillActive(tab, ids));
        }
    }

    /** Which of `ids` the source still lists. When that cannot be asked, all of them — unknown is not gone (NS-3). */
    private async stillActive(tab: RosterTab, ids: string[]): Promise<string[]> {
        try {
            const found = new Set((await this.dataSource.listByIds(tab, ids)).map(record => record.id));
            return ids.filter(id => found.has(id));
        } catch {
            return ids;
        }
    }

    /** Save from the drawer, then reload so the row and the counts show it. A failure is thrown to the drawer. */
    public async saveProvider(draft: RosterProviderDraft): Promise<RosterProvider> {
        const saved = await this.dataSource.saveProvider(draft);
        await this.reload();
        return saved;
    }

    public async saveFacility(draft: RosterFacilityDraft): Promise<RosterFacility> {
        const saved = await this.dataSource.saveFacility(draft);
        await this.reload();
        return saved;
    }

    /** A new record: the drawer opens empty — never with whatever it showed last. */
    public openCreate(): void {
        this.drawerTicket += 1;
        this.drawer = { tab: this.tab, status: 'ready' };
    }

    /**
     * An existing record. Taken from the current page when it is there — it was opened from a row,
     * and the page is what a save just reloaded — and fetched by id otherwise.
     */
    public async openRecord(recordId: string): Promise<void> {
        const ticket = ++this.drawerTicket;
        const tab = this.tab;
        const onPage = this.rows.find(row => row.id === recordId);
        if (onPage) {
            this.drawer = { tab, recordId, status: 'ready', record: onPage };
            return;
        }
        this.drawer = { tab, recordId, status: 'loading' };
        let found: RosterRecord | undefined;
        try {
            [found] = await this.dataSource.listByIds(tab, [recordId]);
        } catch {
            found = undefined;
        }
        if (ticket !== this.drawerTicket) {
            return; // closed, or another record opened, while this one was loading
        }
        runInAction(() => {
            this.drawer = found
                ? { tab, recordId, status: 'ready', record: found }
                : { tab, recordId, status: 'missing' };
        });
    }

    /**
     * After an existing record is saved: ask whether to re-apply it to the claims that reference it.
     * Returns false — and opens nothing — when the data source cannot do that; the caller then just
     * reports the save (NS-4: no affordance without a backing API).
     */
    public startClaimsFlow(ref: RosterRecordRef, name: string): boolean {
        if (!this.dataSource.capabilities.applyToClaims) {
            return false;
        }
        const flow = new RosterClaimsFlow(this.dataSource, ref, name, this.now);
        this.claimsFlow = flow;
        void flow.load();
        return true;
    }

    public closeClaimsFlow(): void {
        this.claimsFlow = undefined;
    }

    public closeDrawer(): void {
        this.drawerTicket += 1;
        this.drawer = undefined;
    }

    /**
     * Reloads after the write whether or not it succeeded: against the real API a bulk write is one
     * request per record, so a failure can come after some records already changed — the list must
     * show what is true now, not what was true before the attempt.
     */
    private async runBulk<T>(write: () => Promise<T>): Promise<T> {
        this.bulkBusy = true;
        try {
            return await write();
        } finally {
            await this.reload();
            runInAction(() => {
                this.bulkBusy = false;
            });
        }
    }

    private filterQuery(): Pick<RosterListQuery, 'search' | 'providerType'> {
        return {
            search: this.search.trim() || undefined,
            providerType: this.tab === 'providers' ? this.providerType : undefined,
        };
    }
}

function messageOf(error: unknown): string {
    return error instanceof Error && error.message ? error.message : 'Request failed';
}
